import { Injectable, Injector, ErrorHandler } from '@angular/core';
import { HttpErrorResponse, HttpResponseBase } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { environment } from '@env/environment';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';

import { NotificationService } from '../notifications';
import { locale as english } from './i18n/en';
import { locale as vietnam } from './i18n/vn';
import { authModuleConfig } from 'app/auth';

/**
 * Application-wide error handler that adds a UI notification to the error handling
 * provided by the default Angular ErrorHandler.
 *
 * @export
 * @class AppErrorHandler
 * @extends {ErrorHandler}
 */
@Injectable()
export class AppErrorHandler extends ErrorHandler {
  constructor(
    private injector: Injector,
    private _translate: TranslateService,
    private _fuseTranslationLoaderService: FuseTranslationLoaderService,
    private notificationsService: NotificationService
  ) {
    super();
    this._fuseTranslationLoaderService.loadTranslations(english, vietnam);
  }

  private goTo(url: string): void {
    setTimeout(() => this.injector.get(Router).navigateByUrl(url));
  }

  private checkStatus(ev: HttpResponseBase): void {
    if (!ev.status || (ev.status >= 200 && ev.status < 300) || ev.status === 401 || ev.status === 400) {
      return;
    }

    const errortext = this.getTranslatedErrorText(ev);
    this.notificationsService.error(`Request error ${ev.status}: ${ev.url} - ${errortext}`);
  }

  private getTranslatedErrorText(ev: HttpResponseBase) {
    return this._translate.instant(`CODEMESSAGE.${ev.status}`);
  }

  private toLogin(): void {
    this.notificationsService.error(`Not logged in or login has expired, please log in again.`);
    this.goTo(authModuleConfig.loginPageRoute);
  }

  private handleData(ev: HttpErrorResponse): Observable<any> {
    this.checkStatus(ev);
    // Business processing: some common operations
    switch (ev.status) {
      case 200:
        break;
      case 401:
        this.toLogin();
        break;
      case 403:
        this.goTo(`/403`);
        break;
      case 404:
      case 500:
        this.goTo(`/exception/${ev.status}`);
        break;
      default:
        if (ev instanceof HttpErrorResponse) {
          console.warn('Unknown errors, mostly caused by the backend does not support cross-domain CORS or invalid configuration', ev);
        } else {
          // throw JS runtime errors
          !environment.production && console.error(ev);
        }
        break;
    }
    if (ev instanceof HttpErrorResponse) {
      return throwError(ev);
    } else {
      return of(ev);
    }
  }

  handleError(error: HttpErrorResponse): void {
    let displayMessage = 'An error occurred.';

    if (!environment.production) {
      displayMessage += ' See console for details.';
    }

    this.handleData(error);
  }
}
